import React, { useState } from 'react';
import { Shift } from '../interfaces';
import { Property } from '../Toolbar';
import { formatPrice, getCurrencySymbol } from '../utils/formatPrice';
import XReportPopup from '../components/XReportPopup';
import './FinalizeDeposit.css';

interface FinalizeDepositProps {
    shift: Shift | null;
    property: Property | null;
    onCloseShift: (nextShiftFloat: number, bankDeposit: number, bankBagNumber: string, clockOutUser: boolean) => void;
    onCancel: () => void;
}

const FinalizeDeposit: React.FC<FinalizeDepositProps> = ({
    shift,
    property,
    onCloseShift,
    onCancel,
}) => {
    const [nextShiftFloat, setNextShiftFloat] = useState<number>(0);
    const [bankDeposit, setBankDeposit] = useState<number>(0);
    const [bankBagNumber, setBankBagNumber] = useState('');
    const [clockOutUser, setClockOutUser] = useState(true);
    const [isXReportOpen, setIsXReportOpen] = useState(false);

    const formatCurrency = (value: number) => {
        return formatPrice(value);
    };

    const currentSales = shift?.totalRevenue ?? 0;

    const handleCloseShift = () => {
        onCloseShift(nextShiftFloat, bankDeposit, bankBagNumber, clockOutUser);
    };

    return (
        <div className="finalize-deposit-page ds-pop">
            <div className="finalize-deposit-container">
                {/* Left Section */}
                <div className="finalize-deposit-main">
                    <h2 className="finalize-deposit-title">Finalize Deposit</h2>

                    {/* Cards Section */}
                    <div className="deposit-cards">
                        {/* Next Shift Float Card */}
                        <div className="deposit-card float-card">
                            <div className="deposit-card-header">
                                <span className="deposit-card-title">Next Shift Float</span>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="9" cy="9" r="6" stroke="#94a3b8" strokeWidth="2"/>
                                    <path d="M9 6V9H12" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="15" cy="15" r="6" stroke="#94a3b8" strokeWidth="2"/>
                                    <path d="M15 12V15H18" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="deposit-input-container">
                                <span className="currency-symbol">{getCurrencySymbol()}</span>
                                <input
                                    type="number"
                                    className="deposit-input"
                                    value={nextShiftFloat}
                                    onChange={(e) => setNextShiftFloat(parseFloat(e.target.value) || 0)}
                                />
                            </div>
                        </div>

                        {/* Bank Deposit Card */}
                        <div className="deposit-card bank-card">
                            <div className="deposit-card-header">
                                <span className="deposit-card-title">Bank Deposit</span>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 21H21M4 18H20M6 18V13M10 18V13M14 18V13M18 18V13M12 3L20 8V13H4V8L12 3Z" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="deposit-input-container">
                                <span className="currency-symbol">{getCurrencySymbol()}</span>
                                <input
                                    type="number"
                                    className="deposit-input negative"
                                    value={bankDeposit}
                                    onChange={(e) => setBankDeposit(parseFloat(e.target.value) || 0)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bank Bag Section */}
                    <div className="bank-bag-section">
                        <label className="bank-bag-label">Bank Bag #</label>
                        <input
                            type="text"
                            className="bank-bag-input"
                            value={bankBagNumber}
                            onChange={(e) => setBankBagNumber(e.target.value)}
                        />

                        <label className="clock-out-checkbox">
                            <input
                                type="checkbox"
                                checked={clockOutUser}
                                onChange={(e) => setClockOutUser(e.target.checked)}
                            />
                            <span>Clock out users?</span>
                        </label>
                    </div>
                </div>

                {/* Right Section */}
                <div className="finalize-deposit-sidebar">
                    <div className="shift-info">
                        <h3 className="shift-number">Z#{shift?.shiftId || '000000'}</h3>
                        <p className="company-name">{property?.displayName}</p>
                    </div>

                    <div className="current-sales">
                        <span>Current Sales:</span>
                        <span className="sales-value">{formatCurrency(currentSales)}</span>
                    </div>

                    <div className="sidebar-actions">
                        <button
                            className="close-shift-btn"
                            onClick={handleCloseShift}
                        >
                            End Day
                        </button>

                        <div className="secondary-actions">
                            <button className="secondary-btn xreport-btn" onClick={() => setIsXReportOpen(true)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 18H4C2.89543 18 2 17.1046 2 16V11C2 9.89543 2.89543 9 4 9H20C21.1046 9 22 9.89543 22 11V16C22 17.1046 21.1046 18 20 18H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                X-Report
                            </button>
                            <button className="secondary-btn cancel" onClick={onCancel}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M21 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <XReportPopup
                isOpen={isXReportOpen}
                onClose={() => setIsXReportOpen(false)}
                shift={shift}
                property={property}
            />
        </div>
    );
};

export default FinalizeDeposit;